import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bot,
  PhoneCall,
  Users2,
  Target,
  MessagesSquare,
  Database,
  FileStack,
  Blocks,
  LineChart,
  ArrowRight,
} from 'lucide-react';
import { Service } from '../types';
import ServiceModal from './ServiceModal';
import { useTilt } from '../hooks/useTilt';
import { openCalendly } from '../utils/loadCalendly';
import '../styles/Services.css';

interface TiltedServiceCardProps {
  service: Service;
  iconColorClass: string;
  onLearnMore: (s: Service) => void;
}

const TiltedServiceCard: React.FC<TiltedServiceCardProps> = ({ service, iconColorClass, onLearnMore }) => {
  const ref = useTilt(4);
  const Icon = service.icon;
  return (
    <article ref={ref} className="service-card reveal">
      <div className={`service-icon-wrap ${iconColorClass}`}>
        <Icon size={20} />
      </div>
      <h3>{service.title}</h3>
      <p>{service.description}</p>
      <ul className="features-list">
        {service.features.map((feature, i) => (
          <li key={i}>{feature}</li>
        ))}
      </ul>
      <button className="btn-ghost service-more" onClick={() => onLearnMore(service)}>
        Learn more <ArrowRight size={14} />
      </button>
    </article>
  );
};

const Services: React.FC = () => {
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const services: Service[] = useMemo(() => [
    {
      id: 1,
      slug: 'agentic-workflow-automation',
      icon: Bot,
      title: 'Agentic Workflow Automation',
      description: 'Autonomous agents that plan, reason, and execute multi-step business processes—with human checkpoints where they matter.',
      features: ['Multi-step reasoning', 'Tool & API calling', 'Human-in-the-loop escalation'],
      fullDescription: [
        'This is our flagship. We build agents that own end-to-end workflows—not single prompts. They read, decide, call the right systems, write back results, and know when to ask a human.',
        'Each agent is built on top of your actual tools (CRM, ERP, ticketing, spreadsheets, internal APIs) so it can actually do the work instead of describing it. We ship with observability, guardrails, retries, and reversible actions baked in.',
        'Perfect for companies that have mapped their SOPs and want them to run on autopilot while keeping a human in the loop for exceptions.',
      ],
      benefits: [
        '80%+ of routine process steps handled without human touch',
        'Full audit trail of every action taken by the agent',
        'Reversible, guardrailed actions with approval flows for critical steps',
        'Drop-in integration with 200+ tools via native connectors',
        'Continual improvement loop from feedback data',
      ],
      useCases: [
        { title: 'Employee onboarding', description: 'Provision accounts, send contracts, schedule sessions, and set up payroll in parallel.' },
        { title: 'Client onboarding', description: 'Collect KYC, generate MSAs, open workspaces, and assign CSMs automatically.' },
        { title: 'Procure-to-pay', description: 'From PO request through invoice reconciliation with GL coding and approvals.' },
        { title: 'Incident response', description: 'Diagnose, page, create postmortems, and file remediation tickets.' },
      ],
      techStack: ['LangGraph', 'Claude', 'GPT-4.1', 'Temporal', 'Postgres', 'Redis', 'Python', 'FastAPI'],
    },
    {
      id: 2,
      slug: 'voice-ai-agents',
      icon: PhoneCall,
      title: 'Voice AI Agents',
      description: 'Conversational AI that handles calls, bookings, and outreach—fluent, fast, and always-on.',
      features: ['Sub-500ms latency', 'Multi-language', 'CRM + calendar sync'],
      fullDescription: [
        'Voice agents that take inbound calls or drive outbound campaigns. They understand intent, sentiment, and urgency; they escalate cleanly; they write notes back to your CRM.',
        'Built for the kind of volume that makes call centres wince: appointment booking, lead qualification, collections, onboarding calls, survey follow-ups.',
      ],
      benefits: [
        '90%+ handling of routine inquiries',
        'Zero wait time, 24/7 availability',
        'Seamless handoff to human agents for complex issues',
        '40+ languages with native-sounding voices',
        'Automatic call summaries written straight to your CRM',
      ],
      useCases: [
        { title: 'Appointment booking', description: 'Live scheduling with real-time calendar availability.' },
        { title: 'Lead qualification', description: 'BANT/MEDDIC qualification on every inbound call.' },
        { title: 'Customer support tier-1', description: 'Order status, FAQs, troubleshooting scripts.' },
        { title: 'Collections reminders', description: 'Polite, compliant payment follow-ups.' },
      ],
      techStack: ['Vapi', 'ElevenLabs', 'Deepgram', 'Twilio', 'LiveKit', 'OpenAI', 'Python'],
    },
    {
      id: 3,
      slug: 'employee-hr-operations',
      icon: Users2,
      title: 'Employee & HR Operations',
      description: 'Automation for the full employee lifecycle—onboarding, payroll, PTO, performance, offboarding.',
      features: ['Onboarding pipelines', 'Payroll + time tracking', 'Performance cycle automation'],
      fullDescription: [
        'HR is one of the highest-leverage places to apply automation: it\'s repetitive, policy-bound, and touches every tool you own. We build flows that span your HRIS, payroll provider, IT, Slack, and docs.',
        'We handle the boring-but-critical stuff: offer letter generation, background checks, account provisioning, benefits enrolment, quarterly reviews, PTO approvals, and compliant offboarding.',
      ],
      benefits: [
        'New hire ready on day-one with zero manual setup',
        'Payroll reconciled and audited every cycle',
        'Performance review prep packaged for managers automatically',
        'GDPR / SOC 2-friendly offboarding with revocation proofs',
        'Slack/Teams nudges for every stuck step',
      ],
      useCases: [
        { title: 'Onboarding orchestration', description: 'Accounts, hardware, buddy assignment, first-week schedule.' },
        { title: 'Payroll automation', description: 'Time-tracking → payroll → GL with exception handling.' },
        { title: 'Performance reviews', description: 'Survey collection, 360 aggregation, calibration packs.' },
        { title: 'PTO & leave', description: 'Approvals, coverage routing, calendar updates.' },
      ],
      techStack: ['Rippling API', 'BambooHR', 'Gusto', 'Deel', 'Slack', 'Google Workspace', 'Python'],
    },
    {
      id: 4,
      slug: 'sales-lead-automation',
      icon: Target,
      title: 'Sales & Lead Automation',
      description: 'Pipeline on autopilot. Enrichment, outbound sequencing, qualification, and CRM hygiene—running in the background.',
      features: ['Intent & enrichment', 'Personalised sequences', 'CRM auto-hygiene'],
      fullDescription: [
        'Sales teams burn 60% of their time on non-selling work. We automate the parts that don\'t need a human voice: research, enrichment, intro-email drafting, CRM updates, and SDR follow-ups.',
        'We blend first-party data, intent signals, and LLM-written context so your AEs only ever get briefed, warm conversations.',
      ],
      benefits: [
        '3–5× increase in outbound touches per rep',
        'CRM data 95%+ complete without manual entry',
        'Enrichment + ICP scoring on every new lead',
        'Auto-drafted personalised intros for every booked meeting',
        'Dead-deal resurrection with intent-based triggers',
      ],
      useCases: [
        { title: 'Outbound sequencer', description: 'ICP-aware cadences with real personalisation.' },
        { title: 'Lead enrichment', description: 'Firmographic + technographic + intent in one pass.' },
        { title: 'Deal-desk automation', description: 'Contract generation, redlines, approvals.' },
        { title: 'CRM hygiene', description: 'Dedupe, stale-deal alerts, forecast rollups.' },
      ],
      techStack: ['Clay', 'Apollo', 'HubSpot', 'Salesforce', 'Instantly', 'Claude', 'Python'],
    },
    {
      id: 5,
      slug: 'customer-support-ai',
      icon: MessagesSquare,
      title: 'Customer Support AI',
      description: 'Tier-1 through tier-3 support agents that resolve, escalate, and learn—bonded to your knowledge base.',
      features: ['Ticket deflection', 'Answer grounding', 'Escalation routing'],
      fullDescription: [
        'Most support tickets are the same 40 questions in different words. We build agents that actually answer them—grounded in your docs, order system, and account data—and escalate gracefully when they shouldn\'t.',
        'Deployed via chat, email, or inside your existing helpdesk (Zendesk, Intercom, Front). Ships with analytics so you can see what customers are actually asking.',
      ],
      benefits: [
        '60–80% deflection on tier-1 volume',
        'First response in seconds, not hours',
        'Every answer cited to a source of truth',
        'Clean handoff with full conversation context',
        'Continuous learning from resolved tickets',
      ],
      useCases: [
        { title: 'Chat + email triage', description: 'Tagging, routing, auto-draft responses.' },
        { title: 'Self-serve answers', description: 'Grounded replies with sources.' },
        { title: 'Refund / shipping policies', description: 'Policy-bound resolutions.' },
        { title: 'VIP escalation', description: 'Automatic tier upgrades for enterprise accounts.' },
      ],
      techStack: ['Claude', 'OpenAI', 'Zendesk', 'Intercom', 'Front', 'Pinecone', 'Python'],
    },
    {
      id: 6,
      slug: 'data-intelligence',
      icon: Database,
      title: 'Data Intelligence & Reporting',
      description: 'Pipelines, warehousing, dashboards, and predictive models—so leadership reads the business, not reports about it.',
      features: ['Warehouse + ELT', 'Executive dashboards', 'Forecasts & anomaly detection'],
      fullDescription: [
        'Most companies have data in fifteen tools and no shared source of truth. We build modern warehouses (BigQuery / Snowflake / DuckDB) with clean ELT, semantic layers, and dashboards that executives actually open.',
        'On top of that we ship predictive models for churn, demand, revenue, and anomaly detection—so you spot problems weeks earlier.',
      ],
      benefits: [
        'Single source of truth across departments',
        '10× faster reporting cycles',
        'Predictive alerts days/weeks ahead',
        'Self-serve analytics for non-technical teams',
        'Live dashboards with audit-ready lineage',
      ],
      useCases: [
        { title: 'Exec KPI dashboard', description: 'Real-time revenue, pipeline, retention.' },
        { title: 'Churn prediction', description: 'Signals + intervention playbooks.' },
        { title: 'Financial consolidation', description: 'Multi-entity P&L without spreadsheet hell.' },
        { title: 'Marketing attribution', description: 'Multi-touch ROI across channels.' },
      ],
      techStack: ['BigQuery', 'Snowflake', 'dbt', 'Fivetran', 'Hex', 'Metabase', 'Python', 'PyTorch'],
    },
    {
      id: 7,
      slug: 'document-knowledge-automation',
      icon: FileStack,
      title: 'Document & Knowledge Automation',
      description: 'Extract, classify, and route any document—contracts, invoices, forms, emails, PDFs—into structured action.',
      features: ['OCR + structured extraction', 'Classification & routing', 'Searchable knowledge base'],
      fullDescription: [
        'Companies leak days of productivity into PDFs. We build pipelines that turn unstructured documents into structured data and decisions.',
        'Same stack powers internal AI knowledge bases: ingesting your drive, wiki, and tickets so employees can ask questions and get cited answers.',
      ],
      benefits: [
        '95%+ field-level accuracy on structured extraction',
        'Human review only for low-confidence cases',
        'Full audit trail of every extracted field',
        'Grounded Q&A over your internal knowledge',
        'Search that actually understands what you mean',
      ],
      useCases: [
        { title: 'Invoice + receipt processing', description: 'Straight to your accounting system.' },
        { title: 'Contract review', description: 'Clause-level extraction and red-flagging.' },
        { title: 'Internal knowledge base', description: 'Cited answers from wikis + drives.' },
        { title: 'Claims & forms intake', description: 'Any PDF → structured JSON → action.' },
      ],
      techStack: ['Claude', 'GPT-4.1', 'Unstructured', 'LlamaIndex', 'Pinecone', 'Postgres', 'FastAPI'],
    },
    {
      id: 8,
      slug: 'custom-ai-integrations',
      icon: Blocks,
      title: 'Custom AI Integrations',
      description: 'Bespoke agents and systems designed for processes generic tools can\'t touch.',
      features: ['Industry-specific models', 'Secure deployment', 'Ongoing optimisation'],
      fullDescription: [
        'Some operations are too specific for off-the-shelf. We partner with teams to design custom AI systems—from computer vision for quality inspection to decisioning engines for complex underwriting.',
        'We own the full loop: problem framing, data preparation, model selection, deployment, monitoring, and continuous improvement. You get a system, not a demo.',
      ],
      benefits: [
        'Purpose-built models on your data',
        'Higher accuracy than generic solutions',
        'You own the system, code, and models',
        'Scalable on your cloud or ours',
        'Ongoing retraining and drift monitoring',
      ],
      useCases: [
        { title: 'Computer vision', description: 'Inspection, counting, defect detection.' },
        { title: 'Decisioning engines', description: 'Underwriting, credit, fraud scoring.' },
        { title: 'Recommendation systems', description: 'Personalised content, products, matching.' },
        { title: 'Domain-specific copilots', description: 'Tools that think in your industry\'s language.' },
      ],
      techStack: ['PyTorch', 'Hugging Face', 'LangChain', 'FastAPI', 'Docker', 'Kubernetes', 'AWS', 'GCP'],
    },
    {
      id: 9,
      slug: 'quant-risk-analytics',
      icon: LineChart,
      title: 'Quant & Risk Analytics',
      description: 'Institutional-grade quantitative modelling for trading, risk management, and portfolio optimisation.',
      features: ['Options pricing', 'Risk engines', 'Backtesting + execution'],
      fullDescription: [
        'Our specialist offering for funds, trading desks, and fintechs. Production-grade analytics for derivatives pricing, risk calculation, and strategy backtesting across asset classes.',
        'We combine mathematical rigour with engineering discipline—battle-tested models that survive contact with live markets.',
      ],
      benefits: [
        '95% reduction in manual risk calculation time',
        'Real-time P&L attribution and risk metrics',
        'Automated stress tests across 100+ scenarios',
        'Multi-asset framework (equities, FX, rates, credit)',
        'Regulatory-ready reporting (Basel III, FRTB, Solvency II)',
      ],
      useCases: [
        { title: 'FX carry risk pipeline', description: 'ATM IV + rate differentials across pairs.' },
        { title: 'Portfolio risk system', description: 'VaR, stress tests, scenario analysis.' },
        { title: 'Volatility surface', description: 'Market-calibrated IV across strikes & tenors.' },
        { title: 'Fixed income analytics', description: 'Yield curves, duration, convexity.' },
      ],
      techStack: ['Python', 'NumPy', 'Pandas', 'QuantLib', 'Bloomberg API', 'IBKR', 'PostgreSQL', 'Redis'],
    },
  ], []);

  const openServiceBySlug = useCallback((slug: string) => {
    const match = services.find((s) => s.slug === slug);
    if (match) {
      setSelectedService(match);
      setIsModalOpen(true);
    }
  }, [services]);

  // Open via URL hash on first load + on hashchange
  useEffect(() => {
    const sync = () => {
      const hash = window.location.hash;
      const m = hash.match(/^#capabilities\/([a-z0-9-]+)$/);
      if (m) {
        openServiceBySlug(m[1]);
      }
    };
    sync();
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, [openServiceBySlug]);

  const handleLearnMore = (service: Service): void => {
    setSelectedService(service);
    setIsModalOpen(true);
    if (window.location.hash !== `#capabilities/${service.slug}`) {
      window.history.pushState(null, '', `#capabilities/${service.slug}`);
    }
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedService(null), 300);
    if (window.location.hash.startsWith('#capabilities/')) {
      window.history.replaceState(null, '', '#capabilities');
    }
  };

  const handleBookConsultation = (): void => {
    const title = selectedService?.title || '';
    closeModal();
    void openCalendly(`https://calendly.com/hello-datryx/30min?a1=${encodeURIComponent(title)}`);
  };

  const iconColorClass = (idx: number): string => {
    const map = ['icon-blue', 'icon-violet', 'icon-cyan', 'icon-mint', 'icon-amber'];
    return map[idx % map.length];
  };

  return (
    <section id="capabilities" className="services section-padding">
      <div className="container">
        <div className="section-kicker reveal">
          <span className="eyebrow">Capabilities</span>
          <h2>A complete agentic operations stack.</h2>
          <p>
            Nine productised services covering the full stack—from one-off automations to
            always-on agents that run entire departments. Mix-and-match, or go end-to-end.
          </p>
        </div>

        <div className="services-grid">
          {services.map((service: Service, idx: number) => (
            <TiltedServiceCard
              key={service.id}
              service={service}
              iconColorClass={iconColorClass(idx)}
              onLearnMore={handleLearnMore}
            />
          ))}
        </div>
      </div>

      <ServiceModal
        service={selectedService}
        isOpen={isModalOpen}
        onClose={closeModal}
        onBookConsultation={handleBookConsultation}
      />
    </section>
  );
};

export default Services;
